import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, AlertTriangle, Pencil, Loader2 } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/training";
import { ROLES } from "@/lib/constants";

const sel = "h-10 rounded-full border border-lasso-line bg-white px-4 text-sm";
const fmt = (v, s = "") => (v === null || v === undefined ? "—" : `${v}${s}`);

export function InsightFilters({ f, setF }) {
  const { user } = useAuth();
  const [regions, setRegions] = useState([]);
  const [comms, setComms] = useState([]);
  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)); api.get("/communities").then((r) => setComms(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value || undefined });
  const visibleComms = comms.filter((c) => (user.role === "admin" ? !f.region_id || c.region_id === f.region_id : c.region_id === user.region_id));
  return (
    <div className="mb-6 flex flex-wrap gap-3" data-testid="insights-filters">
      <input type="date" value={f.date_from || ""} onChange={set("date_from")} className={sel} data-testid="insights-filter-from" />
      <input type="date" value={f.date_to || ""} onChange={set("date_to")} className={sel} data-testid="insights-filter-to" />
      {user.role === "admin" && <select value={f.region_id || ""} onChange={set("region_id")} className={sel} data-testid="insights-filter-region"><option value="">All regions</option>{regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>}
      <select value={f.community_id || ""} onChange={set("community_id")} className={sel} data-testid="insights-filter-community"><option value="">All communities</option>{visibleComms.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
      <select value={f.role || ""} onChange={set("role")} className={sel} data-testid="insights-filter-role"><option value="">All roles</option>{ROLES.filter((r) => r.value !== "admin").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select>
    </div>
  );
}

const Tile = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={testId}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold" data-testid={`${testId}-value`}>{value}</p>
  </div>
);

function QuestionRow({ q, courseId, canEdit }) {
  return (
    <tr className={`border-t border-lasso-line align-top ${q.needs_review ? "bg-rose-50/50" : ""}`} data-testid={`insight-q-${q.id}`}>
      <td className="px-5 py-3">
        <p className="font-medium">{q.prompt}</p>
        <p className="mt-1 text-xs text-emerald-700">Correct: {q.options[q.correct_index]}</p>
        {q.needs_review && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" data-testid={`insight-q-${q.id}-review`}><AlertTriangle className="h-3 w-3" /> Needs review</span>}
      </td>
      <td className="font-mono text-xs">{q.drawn}</td>
      <td className="font-mono text-xs">{q.wrong}/{q.answered}</td>
      <td className="font-semibold" data-testid={`insight-q-${q.id}-pct`}>{q.enough_data ? `${q.pct_missed}%` : <span className="text-xs font-normal text-zinc-400">not enough data</span>}</td>
      <td className="text-xs">{q.top_wrong ? <>{q.top_wrong.option} <span className="text-zinc-400">({q.top_wrong.pct}%)</span></> : "—"}</td>
      <td className="pr-5">{canEdit && <Link to={`/courses/${courseId}/edit#q-${q.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-lasso-deep hover:underline" data-testid={`insight-q-${q.id}-edit`}><Pencil className="h-3 w-3" /> Edit</Link>}</td>
    </tr>
  );
}

export function ExamInsights({ courseId }) {
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/insights/exams/${courseId}`, { params: f }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [courseId, f]);
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  return (
    <div data-testid="exam-insights">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <InsightFilters f={f} setF={setF} />
        <button onClick={() => downloadFile(`/insights/exams/${courseId}/export.csv${qs ? `?${qs}` : ""}`, "Exam-Insights.csv").catch((e) => toast.error(formatApiError(e)))}
          className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-4 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid="insights-export-csv"><FileDown className="h-4 w-4" /> CSV</button>
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="Average score" value={fmt(d.stats.avg_score, "%")} testId="insights-avg-score" />
            <Tile label="Pass rate" value={fmt(d.stats.pass_rate, "%")} testId="insights-pass-rate" />
            <Tile label="Avg attempts to pass" value={fmt(d.stats.avg_attempts_to_pass)} testId="insights-attempts-to-pass" />
            <Tile label="Attempts · learners" value={`${d.stats.attempts} · ${d.stats.learners}`} testId="insights-attempts" />
          </div>
          <div className="mt-4 h-52 rounded-2xl border border-lasso-line bg-white p-4" data-testid="insights-trend">
            <p className="mb-2 text-sm font-semibold">Average score & pass rate (12 weeks)</p>
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={d.stats.trend} margin={{ left: -18, right: 8, top: 4 }}>
                <CartesianGrid stroke="#eef0f3" vertical={false} />
                <XAxis dataKey="week" tickFormatter={(w) => w.slice(5)} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Line dataKey="avg_score" name="Avg score" stroke="#0B0B0C" strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
                <Line dataKey="pass_rate" name="Pass rate" stroke="#82B1D9" strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-lasso-line bg-white">
            <table className="w-full min-w-[760px] text-sm" data-testid="insights-questions-table">
              <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <tr><th className="px-5 py-3">Question (most missed first)</th><th>Drawn</th><th>Wrong</th><th>% missed</th><th>Most common wrong answer</th><th className="pr-5" /></tr>
              </thead>
              <tbody>{d.questions.map((q) => <QuestionRow key={q.id} q={q} courseId={courseId} canEdit={d.course.can_edit} />)}</tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-zinc-500">% missed is shown once a question has at least {d.min_answers} answers. Questions missed by 50% or more are flagged for review. Admin view only: learners never see correct answers here.</p>
        </>
      )}
    </div>
  );
}
